import React, { useState, useEffect } from 'react';
import type { Vendor, CreateVendorData } from '../../services/vendorAPI';
import { X, Save, AlertCircle } from 'lucide-react';

interface VendorFormProps {
  vendor: Vendor | null;
  onClose: () => void;
  onSave: (data: CreateVendorData) => Promise<void>;
}

const SERVICE_CATEGORIES = [
  { value: 'cloud_infrastructure', label: 'Cloud Infrastructure' },
  { value: 'ai_ml_services', label: 'AI/ML Services' },
  { value: 'database', label: 'Database' },
  { value: 'authentication', label: 'Authentication' },
  { value: 'monitoring', label: 'Monitoring' },
  { value: 'source_control', label: 'Source Control' },
  { value: 'ci_cd', label: 'CI/CD' },
  { value: 'security', label: 'Security' },
  { value: 'communication', label: 'Communication' },
  { value: 'productivity', label: 'Productivity' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'payment', label: 'Payment' },
  { value: 'email', label: 'Email' },
  { value: 'storage', label: 'Storage' },
  { value: 'other', label: 'Other' }
];

const DATA_ACCESS_LEVELS = [
  { value: 'full_access', label: 'Full Access - Stores/processes customer data' },
  { value: 'transit_only', label: 'Transit Only - Data passes through but not stored' },
  { value: 'metadata_only', label: 'Metadata Only - Access to metadata/configs only' },
  { value: 'no_access', label: 'No Access - No customer data access' }
];

const VendorForm: React.FC<VendorFormProps> = ({ vendor, onClose, onSave }) => {
  const [formData, setFormData] = useState<Partial<CreateVendorData>>({
    vendor_name: '',
    tier: 2,
    service_description: '',
    service_category: 'other',
    data_access_level: 'no_access',
    soc2_status: 'not_applicable',
    dpa_status: 'not_required',
    iso27001_certified: false,
    zero_data_retention: false,
    ai_training_prohibited: true,
    breach_notification_hours: 72,
    right_to_audit: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (vendor) {
      setFormData({
        vendor_name: vendor.vendor_name,
        tier: vendor.tier,
        service_description: vendor.service_description,
        service_category: vendor.service_category,
        data_access_level: vendor.data_access_level,
        soc2_status: vendor.soc2_status,
        soc2_expiration_date: vendor.soc2_expiration_date || undefined,
        dpa_status: vendor.dpa_status,
        dpa_signed_date: vendor.dpa_signed_date || undefined,
        iso27001_certified: vendor.iso27001_certified,
        security_certifications: vendor.security_certifications,
        data_residency: vendor.data_residency || undefined,
        encryption_at_rest: vendor.encryption_at_rest || undefined,
        encryption_in_transit: vendor.encryption_in_transit || undefined,
        zero_data_retention: vendor.zero_data_retention,
        ai_training_prohibited: vendor.ai_training_prohibited,
        breach_notification_hours: vendor.breach_notification_hours,
        right_to_audit: vendor.right_to_audit,
        contract_start_date: vendor.contract_start_date || undefined,
        contract_end_date: vendor.contract_end_date || undefined,
        contract_value_annual: vendor.contract_value_annual || undefined,
        support_url: vendor.support_url || undefined,
        security_page_url: vendor.security_page_url || undefined,
        trust_center_url: vendor.trust_center_url || undefined,
        status_page_url: vendor.status_page_url || undefined,
        notes: vendor.notes || undefined,
        tags: vendor.tags
      });
    }
  }, [vendor]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;

    let parsedValue: unknown = value;
    if (type === 'checkbox') {
      parsedValue = (e.target as HTMLInputElement).checked;
    } else if (type === 'number') {
      parsedValue = value ? parseInt(value, 10) : undefined;
    }

    setFormData(prev => ({ ...prev, [name]: parsedValue }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.vendor_name?.trim()) {
      newErrors.vendor_name = 'Vendor name is required';
    }

    if (!formData.service_description?.trim()) {
      newErrors.service_description = 'Service description is required';
    }

    if (!formData.service_category) {
      newErrors.service_category = 'Service category is required';
    }

    if (!formData.data_access_level) {
      newErrors.data_access_level = 'Data access level is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsSaving(true);
    try {
      await onSave(formData as CreateVendorData);
    } catch (error) {
      console.error('Failed to save vendor:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {vendor ? 'Edit Vendor' : 'Add New Vendor'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-4">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vendor Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="vendor_name"
                    value={formData.vendor_name || ''}
                    onChange={handleChange}
                    className={`w-full p-2 border rounded-lg ${errors.vendor_name ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="e.g., Amazon Web Services"
                  />
                  {errors.vendor_name && (
                    <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.vendor_name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tier <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="tier"
                    value={formData.tier || 2}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  >
                    <option value={1}>Tier 1 - Critical (handles customer data)</option>
                    <option value={2}>Tier 2 - Standard (no customer data)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="service_category"
                    value={formData.service_category || ''}
                    onChange={handleChange}
                    className={`w-full p-2 border rounded-lg ${errors.service_category ? 'border-red-500' : 'border-gray-300'}`}
                  >
                    {SERVICE_CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Service Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="service_description"
                    value={formData.service_description || ''}
                    onChange={handleChange}
                    rows={3}
                    className={`w-full p-2 border rounded-lg ${errors.service_description ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Describe the services this vendor provides..."
                  />
                  {errors.service_description && (
                    <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.service_description}
                    </p>
                  )}
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Data Access Level <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="data_access_level"
                    value={formData.data_access_level || ''}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  >
                    {DATA_ACCESS_LEVELS.map(level => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-medium text-green-900 mb-4">Compliance Status</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SOC 2 Status</label>
                  <select
                    name="soc2_status"
                    value={formData.soc2_status || 'not_applicable'}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  >
                    <option value="current">Current</option>
                    <option value="expired">Expired</option>
                    <option value="pending">Pending</option>
                    <option value="not_applicable">Not Applicable</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">SOC 2 Expiration</label>
                  <input
                    type="date"
                    name="soc2_expiration_date"
                    value={formData.soc2_expiration_date || ''}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">DPA Status</label>
                  <select
                    name="dpa_status"
                    value={formData.dpa_status || 'not_required'}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  >
                    <option value="signed">Signed</option>
                    <option value="pending">Pending</option>
                    <option value="not_required">Not Required</option>
                    <option value="declined">Declined</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">DPA Signed Date</label>
                  <input
                    type="date"
                    name="dpa_signed_date"
                    value={formData.dpa_signed_date || ''}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>

                <div className="col-span-2 flex items-center gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="iso27001_certified"
                      checked={formData.iso27001_certified || false}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">ISO 27001 Certified</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="font-medium text-purple-900 mb-4">Security Controls</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Encryption at Rest</label>
                  <input
                    type="text"
                    name="encryption_at_rest"
                    value={formData.encryption_at_rest || ''}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., AES-256"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Encryption in Transit</label>
                  <input
                    type="text"
                    name="encryption_in_transit"
                    value={formData.encryption_in_transit || ''}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., TLS 1.3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Data Residency</label>
                  <input
                    type="text"
                    name="data_residency"
                    value={formData.data_residency || ''}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="e.g., US, EU"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Breach Notification (hours)</label>
                  <input
                    type="number"
                    name="breach_notification_hours"
                    value={formData.breach_notification_hours || 72}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    min={1}
                  />
                </div>

                <div className="col-span-2 flex flex-wrap items-center gap-6">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="zero_data_retention"
                      checked={formData.zero_data_retention || false}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Zero Data Retention</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="ai_training_prohibited"
                      checked={formData.ai_training_prohibited !== false}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">AI Training Prohibited</span>
                  </label>

                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="right_to_audit"
                      checked={formData.right_to_audit || false}
                      onChange={handleChange}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Right to Audit</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-medium text-gray-900 mb-4">Resources & URLs</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trust Center URL</label>
                  <input
                    type="url"
                    name="trust_center_url"
                    value={formData.trust_center_url || ''}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="https://trust.example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Security Page URL</label>
                  <input
                    type="url"
                    name="security_page_url"
                    value={formData.security_page_url || ''}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="https://example.com/security"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status Page URL</label>
                  <input
                    type="url"
                    name="status_page_url"
                    value={formData.status_page_url || ''}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="https://status.example.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Support URL</label>
                  <input
                    type="url"
                    name="support_url"
                    value={formData.support_url || ''}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                    placeholder="https://support.example.com"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
              <textarea
                name="notes"
                value={formData.notes || ''}
                onChange={handleChange}
                rows={3}
                className="w-full p-2 border border-gray-300 rounded-lg"
                placeholder="Additional notes about this vendor..."
              />
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSaving}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <Save className="w-4 h-4" />
            {isSaving ? 'Saving...' : vendor ? 'Update Vendor' : 'Create Vendor'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorForm;
